import { useId } from 'react';
import type { ReactElement } from 'react';

import { Marker } from '@xing-com/marker';
import { Hero } from '@xing-com/typography';

import {
  CardActionGroup,
  CardBenefitsList,
  CardContainer,
  CardDiscoverButton,
  CardHeaderSection,
  CardMembershipStatus,
  CardPricing,
} from './sub-components';
import * as Styled from './unpromoted-card.styled';
import type { UnpromotedCardProps } from './unpromoted-card.types';

export type { UnpromotedCardProps } from './unpromoted-card.types';

export const UnpromotedCard = ({
  action,
  activationUrl,
  benefits,
  benefitsTitle,
  cancellation,
  detailsUrl,
  discover,
  marker,
  onActivate,
  onDiscover,
  onFeatureDiscovery,
  owned,
  perks,
  previousPrice,
  price,
  membershipStatusMessage,
  title,
}: UnpromotedCardProps): ReactElement => {
  const isOwned = owned === true;
  const titleId = useId();

  return (
    <CardContainer
      aria-labelledby={titleId}
      as="article"
      size="large"
      padding="regular"
    >
      <CardHeaderSection>
        <Styled.Headline>
          <Hero as="h2" id={titleId} size="small" noMargin>
            {title}
          </Hero>
          {!isOwned && marker ? (
            <Marker ellipsis role="presentation" variant="highlight">
              {marker}
            </Marker>
          ) : null}
        </Styled.Headline>
        {isOwned ? (
          <CardMembershipStatus>
            {membershipStatusMessage ?? ''}
          </CardMembershipStatus>
        ) : (
          <>
            <CardPricing price={price} previousPrice={previousPrice} />
            <CardActionGroup
              action={action}
              activationUrl={activationUrl}
              cancellation={cancellation}
              onActivate={onActivate}
            />
          </>
        )}
      </CardHeaderSection>
      <Styled.BenefitsSection>
        <CardBenefitsList benefits={benefits} benefitsTitle={benefitsTitle} />
        {perks ? (
          <CardDiscoverButton
            action={action}
            activationUrl={activationUrl}
            cancellation={cancellation}
            detailsUrl={detailsUrl}
            discover={discover}
            onActivate={onActivate}
            onDiscover={onDiscover}
            onFeatureDiscovery={onFeatureDiscovery}
            owned={owned}
            perks={perks}
            previousPrice={previousPrice}
            price={price}
            membershipStatusMessage={membershipStatusMessage}
            title={title}
          />
        ) : null}
      </Styled.BenefitsSection>
    </CardContainer>
  );
};
